/**
 * RenewalPathwayCard — Phase 9E guided renewal card.
 *
 * Given `{state, entry_date}` this fetches /api/renewal/config/:state and
 * renders one of three panels:
 *   1. YES  — pro-se renewal viable (guided flow with document preview)
 *   2. NO   — attorney required (static informational panel)
 *   3. EXPIRED (AZ pre-2013) — hard-block warning
 *
 * Emits `onChange` with a `renewal` sub-object that JudgmentFirstForm
 * merges into the debtor draft. Alert-opt-in toggle is persisted.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  BookOpen,
  Calendar,
  ChevronDown,
  FileText,
  Infinity as InfinityIcon,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { apiClient } from "@/lib/apiClient";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaToggle from "@/components/jea/JeaToggle";
import { RenewalPreviewModal } from "./RenewalPreviewModal";
import { cn } from "@/lib/utils";

const short = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const daysBetween = (a, b) => Math.round((new Date(b).getTime() - new Date(a).getTime()) / 86400000);

const addYearsIso = (iso, years) => {
  if (!iso) return null;
  const d = new Date(iso);
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
};

// ---------------------------------------------------------------------------
// Timeline strip
// ---------------------------------------------------------------------------
const TimelineStrip = ({ entryIso, deadlineIso, triggerType }) => {
  if (!entryIso || !deadlineIso) return null;
  const now = new Date().toISOString().slice(0, 10);
  const span = daysBetween(entryIso, deadlineIso) || 1;
  const todayOffset = Math.max(0, Math.min(1, daysBetween(entryIso, now) / span));
  const alertOffset = Math.max(0, Math.min(1, (daysBetween(entryIso, deadlineIso) - 180) / span));

  return (
    <div className="mt-3" data-testid="renewal-timeline-strip">
      <div className="relative h-3 rounded-full bg-bg-tertiary overflow-hidden">
        {/* Alert threshold marker (6 months out) */}
        <div
          className="absolute top-0 bottom-0 w-1"
          style={{ left: `${alertOffset * 100}%`, background: "#F39C12" }}
          title="Alert threshold (6 months before deadline)"
        />
        {/* Deadline end cap */}
        <div className="absolute top-0 bottom-0 w-1 bg-danger" style={{ left: "100%", transform: "translateX(-4px)" }} />
        {/* Today marker */}
        <div
          className="absolute -top-1 -bottom-1 w-1 bg-success"
          style={{ left: `${todayOffset * 100}%`, transform: "translateX(-2px)" }}
          title="Today"
        />
      </div>
      <div className="flex justify-between text-[10px] text-text-secondary jea-mono mt-1.5">
        <span>{short(entryIso)}</span>
        <span className="text-success">Today · {short(now)}</span>
        <span className="text-danger">{triggerType === "dormancy" ? "Dormancy" : "Expires"} · {short(deadlineIso)}</span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// EXPIRED (AZ pre-2013)
// ---------------------------------------------------------------------------
const ExpiredCard = ({ config }) => (
  <JeaCard className="border-danger/60 bg-danger/10" data-testid="renewal-expired-card">
    <div className="flex items-start gap-3">
      <AlertTriangle size={22} className="text-danger shrink-0 mt-0.5" />
      <div>
        <div className="text-white font-bold text-[16px]">Judgment permanently expired</div>
        <div className="text-text-secondary text-[13px] mt-1">{config.message}</div>
        <div className="text-text-muted text-[11px] italic mt-3">
          This platform cannot enforce this judgment. Consult counsel about non-enforcement options.
        </div>
      </div>
    </div>
  </JeaCard>
);

// ---------------------------------------------------------------------------
// NO — neutral "no renewal pathway" (opt-in display mode)
// ---------------------------------------------------------------------------
const NoRenewalPathwayNeutralCard = ({ config }) => (
  <JeaCard data-testid="renewal-no-pathway-neutral-card">
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-[10px] bg-bg-tertiary border border-border-default flex items-center justify-center shrink-0">
        <BookOpen size={18} className="text-text-secondary" />
      </div>
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-white font-bold text-[16px]">
            {config.state_display} — No Renewal Pathway
          </h3>
          <span
            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-text-muted/15 text-text-secondary tracking-[0.06em] uppercase border border-border-default"
            data-testid="renewal-no-pathway-badge"
          >
            No pathway
          </span>
        </div>
        <p className="text-text-secondary text-[13px] mt-2 max-w-[560px]">
          This state does not currently offer a post-judgment renewal mechanism.
        </p>
      </div>
    </div>
  </JeaCard>
);

// ---------------------------------------------------------------------------
// NO — attorney required (default)
// ---------------------------------------------------------------------------
const NoPathwayCard = ({ config, entryIso, renewalDraft, onChange }) => {
  const deadlineIso = entryIso ? addYearsIso(entryIso, config.trigger_window_years || 10) : null;
  return (
    <JeaCard data-testid="renewal-no-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-[10px] bg-warning/15 border border-warning/40 flex items-center justify-center shrink-0">
            <ShieldAlert size={18} className="text-warning" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-white font-bold text-[16px]">{config.state_display} — Attorney Required</h3>
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-warning/15 text-warning tracking-[0.06em] uppercase border border-warning/40"
                data-testid="renewal-no-badge"
              >
                Attorney required
              </span>
            </div>
            <p className="text-text-secondary text-[13px] mt-2 max-w-[560px]">{config.message}</p>
          </div>
        </div>
      </div>
      {deadlineIso && (
        <div className="mt-4 rounded-[8px] bg-bg-tertiary border border-border-default p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-text-secondary text-[13px]">
            <Calendar size={14} className="text-warning" />
            {config.trigger_type === "dormancy" ? "Estimated dormancy" : "Estimated expiration"}:
            <span className="text-white font-semibold jea-mono">{short(deadlineIso)}</span>
          </div>
        </div>
      )}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[12px] text-text-secondary">Enable a 12-month advance warning alert</div>
        <div className="flex items-center gap-2">
          <span className="text-text-secondary text-[11px] jea-caption">Alerts</span>
          <JeaToggle
            checked={renewalDraft.alerts_enabled ?? true}
            onChange={(v) => onChange({ ...renewalDraft, alerts_enabled: v })}
            data-testid="renewal-no-alerts-toggle"
          />
        </div>
      </div>
    </JeaCard>
  );
};

// ---------------------------------------------------------------------------
// YES
// ---------------------------------------------------------------------------
const SpecialFlagBadge = ({ flag }) => {
  const MAP = {
    PA_renew_indefinitely: {
      Icon: InfinityIcon,
      label: "Renew Indefinitely",
      cls: "bg-success/15 text-success border-success/40",
      testid: "renewal-flag-pa-indefinite",
    },
    OH_two_track: {
      Icon: ShieldAlert,
      label: "Two-track filing required",
      cls: "bg-warning/15 text-warning border-warning/40",
      testid: "renewal-flag-oh-two-track",
    },
    AZ_pre_2013_expired_permanent: {
      Icon: AlertTriangle,
      label: "Pre-2013 judgments cannot be revived",
      cls: "bg-danger/10 text-danger border-danger/40",
      testid: "renewal-flag-az-pre2013-note",
    },
    CA_pro_se_benchmark: {
      Icon: Sparkles,
      label: "Pro-se benchmark: EJ-190",
      cls: "bg-accent-pink/10 text-accent-pink border-accent-pink/40",
      testid: "renewal-flag-ca-benchmark",
    },
    KS_track_last_execution_date: {
      Icon: Calendar,
      label: "Track last execution date",
      cls: "bg-accent-pink/10 text-accent-pink border-accent-pink/40",
      testid: "renewal-flag-ks-execution",
    },
    VA_regime_pre_2021: {
      Icon: BookOpen,
      label: "Pre-July 1, 2021 regime",
      cls: "bg-bg-tertiary text-text-secondary border-border-default",
      testid: "renewal-flag-va-pre2021",
    },
    VA_regime_post_2021: {
      Icon: BookOpen,
      label: "Post-July 1, 2021 regime",
      cls: "bg-bg-tertiary text-text-secondary border-border-default",
      testid: "renewal-flag-va-post2021",
    },
  };
  const meta = MAP[flag];
  if (!meta) return null;
  const { Icon } = meta;
  return (
    <span
      className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-[0.06em] uppercase border", meta.cls)}
      data-testid={meta.testid}
    >
      <Icon size={10} />
      {meta.label}
    </span>
  );
};

const YesPathwayCard = ({ resolved, entryIso, renewalDraft, onChange, debtorId }) => {
  const cfg = resolved.config;
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(null);
  const deadlineIso = useMemo(
    () => (entryIso ? addYearsIso(entryIso, cfg.trigger_window_years) : null),
    [entryIso, cfg.trigger_window_years]
  );
  const daysToDeadline = deadlineIso ? daysBetween(new Date().toISOString().slice(0, 10), deadlineIso) : null;

  return (
    <JeaCard className="border-success/40 bg-success/5" data-testid="renewal-yes-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-[10px] bg-success/15 border border-success/40 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} className="text-success" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-white font-bold text-[16px]">
                {resolved.state_display} — Pro-Se Renewal Available
              </h3>
              <span
                className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-success/15 text-success tracking-[0.06em] uppercase border border-success/40"
                data-testid="renewal-yes-badge"
              >
                Pro-se renewal
              </span>
              {resolved.regime_label && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-bg-tertiary text-text-secondary tracking-[0.06em] uppercase border border-border-default"
                  data-testid="renewal-regime-label"
                >
                  {resolved.regime_label} regime
                </span>
              )}
            </div>
            <p className="text-white text-[15px] leading-6 mt-2 max-w-[600px]">{resolved.message}</p>
            {(cfg.special_flags || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {cfg.special_flags.map((f) => <SpecialFlagBadge key={f} flag={f} />)}
              </div>
            )}
          </div>
        </div>
      </div>

      <TimelineStrip entryIso={entryIso} deadlineIso={deadlineIso} triggerType={cfg.trigger_type} />

      <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12px]">
        <div className="rounded-[8px] bg-bg-tertiary border border-border-default p-3">
          <div className="jea-caption">Trigger</div>
          <div className="text-white font-semibold text-[13px] mt-1 capitalize">{cfg.trigger_type}</div>
        </div>
        <div className="rounded-[8px] bg-bg-tertiary border border-border-default p-3">
          <div className="jea-caption">Window</div>
          <div className="text-white font-semibold text-[13px] mt-1">{cfg.trigger_window_years} years</div>
        </div>
        <div className="rounded-[8px] bg-bg-tertiary border border-border-default p-3">
          <div className="jea-caption">{cfg.trigger_type === "dormancy" ? "Dormancy in" : "Expires in"}</div>
          <div className="text-white font-semibold jea-mono text-[13px] mt-1">
            {daysToDeadline != null ? `${daysToDeadline}d` : "—"}
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setExpanded((s) => !s)}
        className="mt-4 w-full text-left flex items-center justify-between rounded-[8px] border border-border-default bg-bg-tertiary p-3 hover:bg-bg-tertiary/80"
        data-testid="renewal-instructions-toggle"
      >
        <div className="flex items-center gap-2">
          <BookOpen size={14} className="text-accent-pink" />
          <span className="text-white font-semibold text-[13px]">Filing instructions</span>
        </div>
        <ChevronDown size={14} className={cn("text-text-secondary transition-transform", expanded && "rotate-180")} />
      </button>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="mt-3 rounded-[8px] border border-border-default bg-bg-primary p-4 text-[13px] text-text-secondary whitespace-pre-line">
              {cfg.filing_instructions}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="mt-5 flex flex-wrap gap-2">
        <JeaButton
          onClick={() =>
            setPreview({
              document_type: cfg.document_type,
              display_name: cfg.document_display_name,
            })
          }
          disabled={!debtorId}
          className="gap-2"
          data-testid="renewal-preview-primary"
        >
          <FileText size={14} /> Preview {cfg.document_display_name}
        </JeaButton>
        {cfg.secondary_document_type && (
          <JeaButton
            variant="outline-pink"
            onClick={() =>
              setPreview({
                document_type: cfg.secondary_document_type,
                display_name: cfg.secondary_document_display_name,
              })
            }
            disabled={!debtorId}
            className="gap-2"
            data-testid="renewal-preview-secondary"
          >
            <FileText size={14} /> Preview {cfg.secondary_document_display_name}
          </JeaButton>
        )}
      </div>

      <div className="mt-5 flex items-center justify-between gap-3 flex-wrap border-t border-border-default pt-4">
        <div className="text-[12px] text-text-secondary">
          Enable automatic alerts for renewal deadlines
        </div>
        <div className="flex items-center gap-2">
          <span className="text-text-secondary text-[11px] jea-caption">Alerts</span>
          <JeaToggle
            checked={renewalDraft.alerts_enabled ?? true}
            onChange={(v) => onChange({ ...renewalDraft, alerts_enabled: v })}
            data-testid="renewal-yes-alerts-toggle"
          />
        </div>
      </div>

      {!debtorId && (
        <div className="mt-3 text-[11px] text-text-muted italic">
          Save this judgment first to unlock document preview.
        </div>
      )}

      <RenewalPreviewModal
        open={!!preview}
        debtorId={debtorId}
        documentType={preview?.document_type}
        documentDisplayName={preview?.display_name}
        onClose={() => setPreview(null)}
      />
    </JeaCard>
  );
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
export const RenewalPathwayCard = ({
  state,
  entryDate,
  priorRenewalRecorded = false,
  debtorId = null,
  renewalDraft,
  onChange,
  onResolve,      // iter-40 — notify parent of the resolved /renewal/config payload
                  // so sibling fields (e.g. the "Already Renewed?" toggle in
                  // JudgmentFirstForm) can hide themselves for NO_PATHWAY states
                  // without issuing a duplicate network call.
}) => {
  const [resolved, setResolved] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!state || !entryDate) {
      setResolved(null);
      onResolve?.(null);
      return;
    }
    setLoading(true);
    apiClient
      .get(`/renewal/config/${state}`, {
        params: { entry_date: entryDate, prior_renewal_recorded: priorRenewalRecorded },
      })
      .then(({ data }) => {
        setResolved(data);
        onResolve?.(data);
      })
      .catch(() => {
        setResolved(null);
        onResolve?.(null);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, entryDate, priorRenewalRecorded]);

  if (!state || !entryDate) {
    return (
      <JeaCard className="text-[13px] text-text-secondary" data-testid="renewal-pathway-empty">
        Select a state and judgment entry date on the previous step to see the renewal pathway for this judgment.
      </JeaCard>
    );
  }
  if (loading || !resolved) {
    return (
      <JeaCard className="min-h-[140px] animate-pulse" data-testid="renewal-pathway-loading">
        <div className="jea-caption">Renewal Pathway</div>
      </JeaCard>
    );
  }

  const draft = renewalDraft || {};

  if (resolved.renewal_pathway === "EXPIRED") return <ExpiredCard config={resolved} />;
  if (resolved.renewal_pathway === "YES") {
    return (
      <YesPathwayCard
        resolved={resolved}
        entryIso={entryDate}
        renewalDraft={draft}
        onChange={onChange}
        debtorId={debtorId}
      />
    );
  }
  // pathway === "NO" — branch by display mode.
  if (resolved.no_pathway_display_mode === "no_pathway") {
    return <NoRenewalPathwayNeutralCard config={resolved} />;
  }
  return <NoPathwayCard config={resolved} entryIso={entryDate} renewalDraft={draft} onChange={onChange} />;
};

export default RenewalPathwayCard;
